import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router';
import { equipmentSchema } from '../../../../common/src/domain/drone/equipment/equipmentSchemas';
import { EquipmentStates } from '../../../../common/src/enums/EquipmentStates';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchGet, openApiFetchPost } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { LocalizedDatePicker } from '../common/LocalizedDatePicker';
import { Content, EditPanel, EditPanelButtons, EditPanelCell, EditPanelRow, ISelectOption, Panel, PanelBody, PanelButton, Tray } from '../elements';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import {
    ValidatingEditPanelSaveButton,
    ValidatingFilterDropdown,
    ValidatingFilterDropdownWithUrl,
    ValidatingForm,
    ValidatingFormContext,
    ValidatingFormInputDate,
    ValidatingFormInputText,
} from '../validatingElements';
import { TopBar } from './Common/TopBar';

function monthsEarlier(date: string | number) {
    const currentDate = moment(date);
    const earlierDate = currentDate.subtract(2, 'month');
    return earlierDate.toDate();
}

interface IFieldDefinition {
    id: string;
    name: string;
    fieldOrder: number;
}

interface IFieldValue {
    equipmentTypeFieldId: string;
    value: string | null;
}

function EquipmentCustomFields(props: {
    equipmentId: string | undefined;
    fieldValues: Record<string, string>;
    onFieldValueChange: (equipmentTypeFieldId: string, value: string) => void;
}) {
    return (
        <ValidatingFormContext.Consumer>
            {(state: { item: { equipmentTypeId?: string } }) => (
                <EquipmentCustomFieldsInner
                    equipmentTypeId={state.item.equipmentTypeId ?? ''}
                    equipmentId={props.equipmentId ?? ''}
                    fieldValues={props.fieldValues}
                    onFieldValueChange={props.onFieldValueChange}
                />
            )}
        </ValidatingFormContext.Consumer>
    );
}

function EquipmentCustomFieldsInner(props: {
    equipmentTypeId: string;
    equipmentId: string;
    fieldValues: Record<string, string>;
    onFieldValueChange: (equipmentTypeFieldId: string, value: string) => void;
}) {
    const { equipmentTypeId, equipmentId, onFieldValueChange } = props;
    const [fields, setFields] = React.useState<IFieldDefinition[]>([]);
    const [loadedTypeId, setLoadedTypeId] = React.useState<string | undefined>(undefined);
    const [loadedValuesForEquipmentId, setLoadedValuesForEquipmentId] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (!equipmentTypeId || equipmentTypeId === loadedTypeId) {
            return;
        }
        void (async () => {
            const response = await openApiFetchGet('/api/equipmentTypes/{equipmentTypeId}/fields', {
                equipmentTypeId,
            });
            if (response?.items) {
                setFields(response.items);
            } else {
                setFields([]);
            }
            setLoadedTypeId(equipmentTypeId);
        })();
    }, [equipmentTypeId, loadedTypeId]);

    React.useEffect(() => {
        if (!equipmentId || equipmentId === loadedValuesForEquipmentId) {
            return;
        }
        void (async () => {
            const response = await openApiFetchGet('/api/equipment/{equipmentId}/fieldValues', {
                equipmentId,
            });
            if (response?.items) {
                for (const fv of response.items) {
                    if (fv.value) {
                        onFieldValueChange(fv.equipmentTypeFieldId, fv.value);
                    }
                }
            }
            setLoadedValuesForEquipmentId(equipmentId);
        })();
    }, [equipmentId, loadedValuesForEquipmentId, onFieldValueChange]);

    if (fields.length === 0) {
        return null;
    }

    const rows: IFieldDefinition[][] = [];
    for (let i = 0; i < fields.length; i += 2) {
        rows.push(fields.slice(i, i + 2));
    }

    return (
        <>
            {rows.map((row, rowIndex) => (
                <EditPanelRow key={rowIndex}>
                    {row.map((field) => (
                        <EditPanelCell key={field.id} rowCount={6}>
                            <label className="ml5 mb5">{field.name}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={props.fieldValues[field.id] ?? ''}
                                onChange={(e) => props.onFieldValueChange(field.id, e.target.value)}
                            />
                        </EditPanelCell>
                    ))}
                </EditPanelRow>
            ))}
        </>
    );
}

export function EquipmentAddEdit(props: { params: { id?: string } }) {
    const translate = useTranslate();
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const [equipmentTypes, setEquipmentTypes] = React.useState<ISelectOption[]>([]);
    const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({});
    const navigate = useNavigate();

    function handleFieldValueChange(equipmentTypeFieldId: string, value: string) {
        setFieldValues((prev) => ({ ...prev, [equipmentTypeFieldId]: value }));
    }

    async function saveFieldValues(equipmentId: string) {
        const values: IFieldValue[] = Object.entries(fieldValues).map(([equipmentTypeFieldId, value]) => ({
            equipmentTypeFieldId,
            value: value || null,
        }));
        if (values.length > 0) {
            await openApiFetchPost('/api/equipment/{equipmentId}/fieldValues', { equipmentId }, { values });
        }
    }

    async function handleSaveClicked(equipmentId: string, shouldGoToDocuments: boolean) {
        await saveFieldValues(equipmentId);
        if (shouldGoToDocuments) {
            navigate(`/equipment/${equipmentId}/documents`);
        } else {
            navigate(`/equipment/${equipmentId}`);
        }
    }

    function handleCancelClicked(equipmentId?: string) {
        if (equipmentId) {
            navigate(`/equipment/${equipmentId}`);
        } else {
            navigate('/equipment');
        }
    }

    React.useEffect(() => {
        void (async () => {
            const responseEquipmentTypes = await openApiFetchGet('/api/equipmentTypes', {});
            if (responseEquipmentTypes) {
                setEquipmentTypes(responseEquipmentTypes.map((item: { id: string; name: string }) => ({ text: item.name, value: item.id })));
            }
        })();
    }, []);

    return (
        <>
            <TopBar equipmentId={props.params.id} activeTab="addEdit" />
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody noPadding>
                            <ValidatingForm
                                schema={equipmentSchema}
                                createUrl={'/api/equipment/'}
                                updateUrl={`/api/equipment/${props.params.id}`}
                                itemUrl={`/api/equipment/${props.params.id}`}
                                id={props.params.id}
                                onSubmitted={async (id) => {
                                    if (props.params.id) {
                                        await handleSaveClicked(props.params.id, false);
                                    } else {
                                        await handleSaveClicked(id, true);
                                    }
                                }}
                            >
                                <EditPanel>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>
                                            <ValidatingFormInputText name="name" topLabel={translate('common.name')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    {isAdmin && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <ValidatingFilterDropdownWithUrl
                                                    name="clientCompanyId"
                                                    topLabel={translate('common.client')}
                                                    placeholder={translate('common.selectItem', translate('common.client'))}
                                                    url={'/api/clients'}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFilterDropdown
                                                name="status"
                                                topLabel="Status"
                                                placeholder={translate('common.selectItem', translate('common.status'))}
                                                selectOptions={[
                                                    ...[
                                                        { value: EquipmentStates.InUse, text: translate(`equipment.${EquipmentStates.InUse}`) },
                                                        {
                                                            value: EquipmentStates.InMaintenance,
                                                            text: translate(`equipment.${EquipmentStates.InMaintenance}`),
                                                        },
                                                        {
                                                            value: EquipmentStates.OutOfService,
                                                            text: translate(`equipment.${EquipmentStates.OutOfService}`),
                                                        },
                                                        {
                                                            value: EquipmentStates.UnderConstruction,
                                                            text: translate(`equipment.${EquipmentStates.UnderConstruction}`),
                                                        },
                                                    ],
                                                    ...(isAdmin
                                                        ? [
                                                              {
                                                                  value: EquipmentStates.Deleted,
                                                                  text: translate(`equipment.${EquipmentStates.Deleted}`),
                                                              },
                                                          ]
                                                        : []),
                                                ]}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFilterDropdown
                                                name="equipmentTypeId"
                                                topLabel={translate('equipment.types')}
                                                placeholder={translate('common.selectItem', translate('equipment.types'))}
                                                selectOptions={equipmentTypes}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="manufacturer" topLabel={translate('equipment.manufacturer')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="typeIndication" topLabel={translate('equipment.typeIndication')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="softwareVersion" topLabel={translate('equipment.softwareVersion')} />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputText name="serialNumber" topLabel={translate('equipment.serialNumber')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormInputDate
                                                name="dateEndCertificateValidity"
                                                topLabel={translate('equipment.expirationDate')}
                                            />
                                        </EditPanelCell>
                                        <EditPanelCell rowCount={6}>
                                            <ValidatingFormContext.Consumer>
                                                {(state: {
                                                    item: {
                                                        dateReminderRecertification?: string | number;
                                                        dateEndCertificateValidity?: string | number;
                                                    };
                                                    onChange: (name: string, value: Date | null | undefined) => void;
                                                }) => (
                                                    <>
                                                        <label className="ml5 mb5">{translate('equipment.reminderDate')} </label>
                                                        <label htmlFor="dateReminderRecertification">
                                                            <LocalizedDatePicker
                                                                id="dateReminderRecertification"
                                                                value={
                                                                    !state.item.dateReminderRecertification
                                                                        ? !state.item.dateEndCertificateValidity
                                                                            ? null
                                                                            : monthsEarlier(state.item.dateEndCertificateValidity)
                                                                        : new Date(state.item.dateReminderRecertification)
                                                                }
                                                                onChange={(value) => state.onChange('dateReminderRecertification', value)}
                                                            />
                                                        </label>
                                                    </>
                                                )}
                                            </ValidatingFormContext.Consumer>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={12}>
                                            <ValidatingFormInputText name="remark" topLabel={translate('common.remark')} />
                                        </EditPanelCell>
                                    </EditPanelRow>
                                    <EquipmentCustomFields
                                        equipmentId={props.params.id}
                                        fieldValues={fieldValues}
                                        onFieldValueChange={handleFieldValueChange}
                                    />
                                </EditPanel>
                                <EditPanelButtons footer>
                                    <PanelButton text={translate('common.cancel')} onClick={() => handleCancelClicked(props.params.id)} />
                                    <ValidatingEditPanelSaveButton />
                                </EditPanelButtons>
                            </ValidatingForm>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
